import { Component, ViewChild } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeWrite } from 'src/app/shared/models/attributes/attributeWrite';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { AttributesEditorDataSource } from '../attributes-editor/attributesEditorDataSource';
import { DialogDirective } from 'src/shared-app-portal/components/dialog/dialog.directive';
import { IGenericAttributeService } from 'src/shared-app-portal/interfaces/i-generic-attribute-service';
import { AttributesEditorComponent } from '../attributes-editor/attributes-editor.component';

@Component({
  selector: 'app-custom-field-editor-modal',
  templateUrl: './custom-field-editor-modal.component.html',
  styleUrls: ['./custom-field-editor-modal.component.scss'],
  standalone: false
})
export class CustomFieldEditorModalComponent {
  public editorDataSource = new AttributesEditorDataSource();
  public isDocument: boolean = false;
  private isComplexField: boolean = false;

  genericAttributeService: IGenericAttributeService;

  @ViewChild(DialogDirective, { static: true })
  dialog: DialogDirective;

  @ViewChild(AttributesEditorComponent, { static: false })
  attributesEditor: AttributesEditorComponent;

  constructor() {}

  public open(customFieldValues: Attribute[], genericAttributeService: IGenericAttributeService) {
    // Calculated fields are computed by the backend formula evaluator; their stored values are
    // overwritten on the next recalc. Opening this editor for one would let a user "edit" a value
    // that disappears on the next recalc — confusing UX and a data-integrity hazard. Callers
    // should filter calc-field affordances upstream; this is a backstop that fails loudly if
    // they don't.
    if (genericAttributeService.attributeDefinition.uiType === AttributeUiType.CalculatedField) {
      throw new Error(
        `CustomFieldEditorModal cannot edit a CalculatedField (attributeDefinitionId=${genericAttributeService.attributeDefinition.id}). ` +
          'Calc-field values are derived from formulas — caller must suppress edit affordances upstream.'
      );
    }
    this.genericAttributeService = genericAttributeService;

    this.editorDataSource = new AttributesEditorDataSource();
    this.editorDataSource.load(
      cloneDeep(customFieldValues),
      [this.genericAttributeService.attributeDefinition],
      this.filterAttribute(this.genericAttributeService.attributeDefinition)
    );

    this.isComplexField = false;
    this.isDocument = false;

    let yesText = 'Save';
    let noText = 'Cancel';
    let isAffirmativeBasicColor = false;
    if (this.genericAttributeService.attributeDefinition.uiType == AttributeUiType.Document) {
      this.isDocument = true;
      yesText = 'Close';
      noText = '';
      isAffirmativeBasicColor = true;
    } else if (this.genericAttributeService.attributeDefinition.uiType == AttributeUiType.Complex) {
      this.isComplexField = true;
    }

    this.dialog.open({
      header: this.genericAttributeService.attributeDefinition.name,
      noText,
      yesText,
      isAffirmativeBasicColor
    });
  }

  private filterAttribute = (customField: AttributeDefinition) => (attributes: Attribute[]) =>
    attributes.filter((a) => a.attributeDefinitionId == customField.id);

  public onOkay = async (): Promise<boolean> => {
    if (!this.editorDataSource.profileIsValid) {
      this.attributesEditor.markAllAsTouched();
      return Promise.resolve(false);
    }
    if (!this.isDocument) {
      // No need to re-send document updates
      // Document updates are sent with onBlur (doSaveDocument) event.
      const result = await this.sendUpdate();
      return !!result;
    }

    return Promise.resolve(true);
  };

  /** some APIs are wired up to promises. If they are we can keep modal open while the API is called.
   * If not return true and the modal will close while the subscription/observable runs */
  private sendUpdate = async (): Promise<boolean> => {
    if (this.isComplexField) {
      const complexAttributeWriters = this.editorDataSource.asComplexAttributeWrites()[0];
      if (complexAttributeWriters.length > 0) {
        this.genericAttributeService.updateAttributeComplex(complexAttributeWriters);
      } else {
        this.genericAttributeService.deleteAttribute();
      }
      return true;
    }
    const attributeWriter = AttributeWrite.fromAttributes(this.editorDataSource.editable);
    if (attributeWriter) {
      return await this.genericAttributeService.updateAttribute(attributeWriter);
    }
    this.genericAttributeService.deleteAttribute();
    return true;
  };

  public doSaveDocument = (attributeChanged = true) => {
    if (attributeChanged == false) {
      return;
    }

    if (this.isDocument) {
      this.sendUpdate();
    }
  };
}
